import React, { useState, useEffect, useRef } from 'react';
import getValue from 'lodash.get';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import Backdrop from '@mui/material/Backdrop';
import CircularProgress from '@mui/material/CircularProgress';
import Alert from '@mui/material/Alert';
import idScan from './assets/id-scan.json';
import face from './assets/face.json';
import SectionContainer from '../layout/SectionContainer.jsx';
import SectionNav from '../layout/SectionNav.jsx';
import WelcomeScreen from '../layout/WelcomeScreen.jsx';
import ContentContainer from '../layout/ContentContainer.jsx';
import Field from '../layout/Field.jsx';
import { cleanIdCardNumber } from '../util/idCard.js';
import { getTextDefaults } from './translations/IDCardSectionTranslation.js';
import { useIdDocumentCapture } from '../hooks/useIdDocumentCapture.jsx';
import { getFlagMessages } from './translations/documentFlagMessages.js';
import SelfieCamera from '../components/SelfieCamera.jsx';
import { getTextDefaults as getFaceTextDefaults } from './translations/FaceSectionTranslation.js';

const WelcomePage = ({ text }) => {
	return (
		<WelcomeScreen animation={idScan}>
			<Typography variant="h6">{text.welcomeTitle}</Typography>
			<Typography>{text.welcomeReason}</Typography>
		</WelcomeScreen>
	);
};

const PersonalInfoPage = ({
	Registry,
	fields = [],
	values = {},
	errors = {},
	onChange,
	text,
	disabled,
	documentMetadataFields = [],
}) => {
	const Fields = fields.map((field, i) => (
		<Field
			Registry={Registry}
			{...field}
			key={`${field.name}-${i}`}
			onChange={onChange}
			values={values}
			errors={errors}
			disabled={disabled || documentMetadataFields.includes(field.name)}
		/>
	));

	return (
		<ContentContainer>
			<Typography variant="h6" sx={{ marginBottom: 2 }}>
				{text.personalInfoTitle}
			</Typography>
			<Typography sx={{ marginBottom: 3 }}>{text.personalInfoDescription}</Typography>
			{Fields}
		</ContentContainer>
	);
};

const SelfieSectionWelcome = ({ text }) => {
	return (
		<WelcomeScreen animation={face}>
			<Typography variant="h6">{text.selfieTitle}</Typography>
			<Typography>{text.selfieDescription}</Typography>
		</WelcomeScreen>
	);
};

const normalizeAcceptedDocuments = (val) => {
	if (Array.isArray(val)) return val.filter(Boolean);
	if (!val || typeof val !== 'object') return ['id', 'passport'];
	// Clean object format from CheckGroup: { id: true, passport: true }
	const fromTrueKeys = Object.keys(val).filter(k => val[k] === true && k !== '' && isNaN(Number(k)));
	if (fromTrueKeys.length > 0) return fromTrueKeys;
	// Corrupted format from array-spread bug: { "0": "id", "1": "passport", "": true }
	return Object.values(val).filter(v => typeof v === 'string');
};

const IDCardSectionAgentic = ({
	Registry,
	acceptedDocuments = ['id', 'passport'],
	fieldSelection = {},
	onChange = () => {},
	values = {},
	errors = {},
	labels = {},
	textOverride = {},
	uploadUrl = '/api/files',
	background = 'light',
	selectedLanguage = 'EN',
	noWelcome = false,
	addPendingAction = () => {},
	removePendingAction = () => {},
	useCamera = true,
	cameraIncludes = '',
	selfieCameraIncludes = '',
	cameraRotationAngle = 0,
	disabled = false,
	requireSelfie = true,
	selfieName = 'userFace',
	overrideExistingUserData = false,
	navigationProps,
}) => {
	const text = Object.assign({}, getTextDefaults(selectedLanguage), textOverride);
	const acceptedDocumentsList = normalizeAcceptedDocuments(acceptedDocuments);
	const documentValue = getValue(values, 'userDocuments') || [];
	const [currentScreen, setCurrentScreen] = useState(noWelcome ? 'id-capture' : 'welcome');
	// selectedType + the re-derive effect now live in useIdDocumentCapture.
	const [isLoading, setLoading] = useState(false);
	const [hasPrefilledData, setHasPrefilledData] = useState(false);
	const [documentMetadataFields, setDocumentMetadataFields] = useState([]);

	// Use local state to buffer document uploads to prevent Form resets during upload
	const [localDocuments, setLocalDocuments] = useState(documentValue);
	const isUploadingRef = useRef(false);

	const faceText = getFaceTextDefaults(selectedLanguage);

	// Disable constant re-rendering of the document upload section
	useEffect(() => {
		if (!isUploadingRef.current && documentValue.length > 0) {
			setLocalDocuments(documentValue);
		}
	}, [documentValue]);

	useEffect(() => {
		if (currentScreen === 'selfie-submit') {
			setCurrentScreen();
			navigationProps.next({ preventDefault: () => {} });
		}
	}, [currentScreen, navigationProps]);

	// Prefill personal info from uploaded document when document is uploaded
	useEffect(() => {
		const docsToCheck = localDocuments.length > 0 ? localDocuments : documentValue;

		if (hasPrefilledData) {
			return;
		}

		if (!docsToCheck || docsToCheck.length === 0) {
			return;
		}

		const uploadedDoc = docsToCheck[0];
		if (!uploadedDoc || !uploadedDoc.metadata) {
			return;
		}

		const metadata = uploadedDoc.metadata;
		const userData = getValue(values, 'userData') || {};
		const prefillPrefix = uploadedDoc.status == 'accepted' ? 'userProps' : 'userInReviewProps';

		const fieldsToUpdate = [];
		const metadataFieldsList = [];

		if (metadata.firstName) {
			metadataFieldsList.push(`${prefillPrefix}.firstName`);
			if (overrideExistingUserData || !userData.firstName) {
				fieldsToUpdate.push({ name: `${prefillPrefix}.firstName`, value: metadata.firstName });
			}
		}

		if (metadata.lastName) {
			metadataFieldsList.push(`${prefillPrefix}.lastName`);
			if (overrideExistingUserData || !userData.lastName) {
				fieldsToUpdate.push({ name: `${prefillPrefix}.lastName`, value: metadata.lastName });
			}
		}

		if (metadata.dateOfBirth) {
			metadataFieldsList.push(`${prefillPrefix}.dateOfBirth`);
			if (overrideExistingUserData || !userData.dateOfBirth) {
				fieldsToUpdate.push({ name: `${prefillPrefix}.dateOfBirth`, value: metadata.dateOfBirth });
			}
		}

		if (metadata.documentNumber) {
			metadataFieldsList.push(`${prefillPrefix}.idCardNumber`);
			if (overrideExistingUserData || !userData.idCardNumber) {
				fieldsToUpdate.push({ name: `${prefillPrefix}.idCardNumber`, value: metadata.documentNumber });
			}
		}

		if (fieldsToUpdate.length > 0) {
			const updatedProps = { ...getValue(values, prefillPrefix) };

			fieldsToUpdate.forEach((field) => {
				const propName = field.name.replace(`${prefillPrefix}.`, '');
				updatedProps[propName] = field.value;
			});

			onChange({
				target: {
					name: prefillPrefix,
					value: updatedProps,
				},
			});
		}

		setDocumentMetadataFields(metadataFieldsList);
		setHasPrefilledData(true);
	}, [localDocuments, documentValue]);

	const onDocumentChangeHandler = (index, processedDoc) => {
		const newValue = [...localDocuments];
		newValue[index] = processedDoc;
		setLocalDocuments(newValue);

		// Mark that we're done uploading
		isUploadingRef.current = false;

		// A newly verified document must re-run prefill, so clear the one-shot latch and locked-field set.
		if (processedDoc?.metadata) { setHasPrefilledData(false); setDocumentMetadataFields([]); }

		return onChange({
			target: {
				name: 'userDocuments',
				value: newValue,
			},
		});
	};

	const onFieldChangeHandler = ({ target }) => {
		// Clean ID card number if this is the idCardNumber field
		const isIdCardField = target.name.endsWith('.idCardNumber');
		const cleanedValue = isIdCardField ? cleanIdCardNumber(target.value) : target.value;

		return onChange({
			target: {
				name: `${target.name}`,
				value: cleanedValue,
			},
		});
	};

	const handleSelfieComplete = (evt) => {
		onChange(evt);
		setCurrentScreen('selfie-submit');
	};

	const handleBackFromSelfie = () => {
		setCurrentScreen('selfie-welcome');
	};

	// Nested under `userData` because the backend's buildAgenticContext reads
	// context.userData.{firstName,lastName,dateOfBirth}; a flat object would reach the agent empty.
	const uploadContext = {
		userData: {
			firstName: getValue(values, 'userData.firstName'),
			lastName: getValue(values, 'userData.lastName'),
			dateOfBirth: getValue(values, 'userData.dateOfBirth'),
		},
	};

	const guided = useIdDocumentCapture({
		acceptedTypes: acceptedDocumentsList,
		document: documentValue[0],
		onDocument: (parsedFile) => onDocumentChangeHandler(0, parsedFile),
		text,
		uploadUrl,
		context: uploadContext,
		reviewMessages: getFlagMessages(selectedLanguage),
		useCamera,
		forceCamera: typeof window !== 'undefined' && window.location.href.includes('deviceId='),
		cameraIncludes,
		cameraRotationAngle,
		disabled,
		addPendingAction: (action) => {
			isUploadingRef.current = true;
			addPendingAction(action);
		},
		removePendingAction,
	});

	const handleNext = (event) => {
		if (currentScreen === 'welcome') {
			setCurrentScreen('id-capture');
			return;
		}

		if (currentScreen === 'id-capture') {
			// Only the capture sub-screen advances; on 'type', selecting a card auto-advances internally.
			if (guided.subScreen === 'capture') setCurrentScreen('personal-info');
			return;
		}

		if (currentScreen === 'personal-info') {
			// Check if selfie is required
			if (requireSelfie) {
				setCurrentScreen('selfie-welcome');
				return;
			}
			return navigationProps.next(event);
		}

		if (currentScreen === 'selfie-welcome') {
			setCurrentScreen('selfie-camera');
			return;
		}
	};

	const handlePrevious = (event) => {
		if (currentScreen === 'selfie-welcome') {
			return setCurrentScreen('personal-info');
		}

		if (currentScreen === 'personal-info') {
			return setCurrentScreen('id-capture'); // hook still on 'capture', so the captured doc shows again
		}

		if (currentScreen === 'id-capture') {
			if (guided.goBack()) return; // capture → type (multi-type); consumes the press
			if (!noWelcome) return setCurrentScreen('welcome');
			return navigationProps.previous(event);
		}

		return navigationProps.previous(event);
	};

	// Determine if document is accepted or in review
	const documentStatus = documentValue[0]?.status;
	const isDocumentAccepted = !documentStatus || documentStatus === 'accepted';
	const fieldPrefix = isDocumentAccepted ? 'userProps' : 'userInReviewProps';

	// Get userData and document metadata
	const userData = getValue(values, 'userData') || {};
	const documentMetadata = documentValue[0]?.metadata || {};

	const personalInfoFields = [
		{
			type: 'TextField',
			name: `${fieldPrefix}.firstName`,
			label: text.firstNameLabel,
			required: true,
			value: documentMetadata.firstName,
		},
		{
			type: 'TextField',
			name: `${fieldPrefix}.lastName`,
			label: text.lastNameLabel,
			required: true,
			value: documentMetadata.lastName,
		},
	];

	if (documentMetadata.dateOfBirth && fieldSelection.dateOfBirth?.visible) {
		personalInfoFields.push({
			type: 'DatePicker',
			name: `${fieldPrefix}.dateOfBirth`,
			label: text.dateOfBirthLabel,
			required: true,
			value: documentMetadata.dateOfBirth,
		});
	}

	if (documentMetadata.documentNumber && fieldSelection.idCardNumber?.visible) {
		personalInfoFields.push({
			type: 'TextField',
			name: `${fieldPrefix}.idCardNumber`,
			label: text.idCardNumberLabel,
			required: true,
			value: documentMetadata.documentNumber,
		});
	}

	if (fieldSelection.phone?.visible) {
		personalInfoFields.push({
			type: 'PhoneField',
			name: `${fieldPrefix}.phone`,
			label: text.phoneLabel,
			required: !!fieldSelection.phone?.required,
			allowFixedLine: !!fieldSelection.phone?.allowFixedPhoneLine,
		});
	}

	if (fieldSelection.licensePlate?.visible) {
		personalInfoFields.push({
			type: 'TextField',
			name: `${fieldPrefix}.extraUserProps.License Plate`,
			label: text.licensePlateLabel,
			required: !!fieldSelection.licensePlate?.required,
		});
	}

	if (fieldSelection.company?.visible) {
		personalInfoFields.push({
			type: 'TextField',
			name: `${fieldPrefix}.extraUserProps.Company`,
			label: text.companyLabel,
			required: !!fieldSelection.company?.required,
		});
	}

	// Check if all required personal info fields are filled
	const arePersonalFieldsValid = () => {
		for (const field of personalInfoFields) {
			if (field.required) {
				const fieldNameWithoutPrefix = field.name.replace(/^(userProps|userInReviewProps)\./, '');

				const userDataValue = getValue(userData, fieldNameWithoutPrefix);
				const fieldValue = getValue(values, field.name);

				if (!(userDataValue || fieldValue)) {
					return false;
				}
			}
		}
		return true;
	};

	const canContinue = () => {
		if (currentScreen === 'welcome') return true;
		if (currentScreen === 'id-capture') return guided.canContinue;
		if (currentScreen === 'personal-info') return arePersonalFieldsValid();
		return true;
	};

	const hasPrevious =
		navigationProps.hasPrevious ||
		(currentScreen === 'id-capture' && (guided.subScreen === 'capture' || !noWelcome)) ||
		currentScreen === 'personal-info' ||
		currentScreen === 'selfie-welcome';

	return (
		<SectionContainer background={background}>
			{currentScreen === 'welcome' && (
				<>
					<WelcomePage text={text} />
					<SectionNav
						{...navigationProps}
						hasPrevious={hasPrevious}
						canContinue={canContinue()}
						hasNext={true}
						next={handleNext}
						selectedLanguage={selectedLanguage}
						previous={handlePrevious}
					/>
				</>
			)}

			{currentScreen === 'id-capture' && (
				<>
					{guided.node}
					<SectionNav
						{...navigationProps}
						hasPrevious={hasPrevious}
						canContinue={canContinue()}
						hasNext={true}
						next={handleNext}
						selectedLanguage={selectedLanguage}
						previous={handlePrevious}
					/>
				</>
			)}

			{currentScreen === 'personal-info' && (
				<>
					<PersonalInfoPage
						Registry={Registry}
						fields={personalInfoFields}
						values={values}
						errors={errors}
						onChange={onFieldChangeHandler}
						text={text}
						disabled={disabled}
						documentMetadataFields={documentMetadataFields}
					/>
					<SectionNav
						{...navigationProps}
						hasPrevious={hasPrevious}
						canContinue={canContinue()}
						hasNext={true}
						next={handleNext}
						selectedLanguage={selectedLanguage}
						previous={handlePrevious}
					/>
				</>
			)}

			{/* Selfie Welcome Screen */}
			{currentScreen === 'selfie-welcome' && requireSelfie && (
				<>
					<SelfieSectionWelcome text={text} />
					<SectionNav
						{...navigationProps}
						hasPrevious={hasPrevious}
						canContinue={true}
						hasNext={true}
						next={handleNext}
						selectedLanguage={selectedLanguage}
						previous={handlePrevious}
					/>
				</>
			)}

			{/* Selfie Camera - self-contained flow: preview → evaluate → result */}
			{currentScreen === 'selfie-camera' && requireSelfie && (
				<Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', width: '100%' }}>
					<SelfieCamera
						cameraIncludes={selfieCameraIncludes}
						onBackPress={handleBackFromSelfie}
						onComplete={handleSelfieComplete}
						uploadUrl={uploadUrl}
						name={selfieName}
						addPendingAction={addPendingAction}
						removePendingAction={removePendingAction}
						text={faceText}
						getTextDefaults={getFaceTextDefaults}
					/>
				</Box>
			)}

			<Backdrop open={isLoading}>
				<CircularProgress />
			</Backdrop>
		</SectionContainer>
	);
};

IDCardSectionAgentic.type = 'IDCardSectionAgentic';
IDCardSectionAgentic.hasNavigation = true;
IDCardSectionAgentic.spec = [
	{
		name: 'IDCardSectionAgentic',
		key: 'IDCardSectionAgentic',
		title: 'ID Card Identification Section (Agentic)',
		translatable: ['title', 'navigation.previousLabel', 'navigation.nextLabel', 'navigation.submitLabel'],
		allowedFields: [],
		fields: [
			// ── Basic ──────────────────────────────────────────────────────
			{ type: 'SettingsHeader', title: 'Basic' },
			{
				type: 'TextField',
				name: 'title',
				label: 'Section title',
			},
			{
				type: 'CheckGroup',
				name: 'acceptedDocuments',
				label: 'Accepted documents',
				rowDescription: 'The user can upload any one of these document types. At least one must be selected.',
				options: [
					{ value: 'id', label: 'National ID' },
					{ value: 'passport', label: 'Passport' },
					{ value: 'residence-permit', label: 'Residence Permit' },
				],
				required: true,
			},

			// ── Optional profile fields ─────────────────────────────────────
			{ type: 'SettingsHeader', title: 'Optional profile fields' },
			{
				type: 'Checkbox',
				name: 'fieldSelection.dateOfBirth.visible',
				label: 'Date of birth',
			},
			{
				type: 'Checkbox',
				name: 'fieldSelection.idCardNumber.visible',
				label: 'ID card number',
			},
			{
				type: 'CheckboxWithToggle',
				name: 'fieldSelection.phone.visible',
				requiredName: 'fieldSelection.phone.required',
				label: 'Phone',
			},
			{
				type: 'Checkbox',
				label: 'Allow fixed phone line',
				name: 'fieldSelection.phone.allowFixedPhoneLine',
			},
			{
				type: 'CheckboxWithToggle',
				name: 'fieldSelection.licensePlate.visible',
				requiredName: 'fieldSelection.licensePlate.required',
				label: 'License plate',
			},

			// ── Behaviour ──────────────────────────────────────────────────
			{ type: 'SettingsHeader', title: 'Behaviour' },
			{
				type: 'Checkbox',
				label: 'Require selfie for identification?',
				name: 'requireSelfie',
			},
			{
				type: 'Checkbox',
				label: 'Override existing profile data with document data?',
				name: 'overrideExistingUserData',
			},
		],
		defaultProperties: {
			title: 'ID Card Identification',
			noWelcome: false,
			requireSelfie: true,
			overrideExistingUserData: false,
			acceptedDocuments: { id: true, passport: true },
			cameraIncludes: '',
			selfieCameraIncludes: '',
			cameraZoom: 1,
			fieldSelection: {
				phone: { visible: false, required: false, allowFixedPhoneLine: false },
				dateOfBirth: { visible: false },
				idCardNumber: { visible: false },
				licensePlate: { visible: false, required: false },
			},
			refDataMap: {
				'userData.firstName': 'user.firstName',
				'userData.lastName': 'user.lastName',
				'userData.dateOfBirth': 'user.userData.dateOfBirth',
				'userData.idCardNumber': 'user.userData.idCardNumber',
				'userData.extraUserProps.License Plate': 'user.userData.extraUserProps.License Plate',
				'userData.extraUserProps.Company': 'user.userData.extraUserProps.Company',
			},
		},
	},
];

export default IDCardSectionAgentic;
